import React, { useEffect, useState } from 'react';
import {
  Box,
  Center,
  Checkbox,
  Flex,
  Spinner,
  Stack,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import AnalysisFilterActions from './AnalysisFilterActions';
import StationService from '../../../services/StationService';
import ResidentService from '../../../services/ResidentService';
import { IStation } from '../../../interfaces/Station/IStation';
import { IResidentListItem } from '../../../interfaces/Resident/IResidentListItem';
import { useTranslation } from 'react-i18next';
import { getLanguageKeyByType } from '../../../utils/getLanguageKeyByType';
import { useAuth } from '../../../context/AuthContext';

interface LivingArea {
  id: string;
  name: string;
}

interface ResidentFilterProps {
  onCancel: () => void;
  onApply: (
    selectedStations: string[],
    selectedResidents: string[],
    stationNames?: Record<string, string>
  ) => void;
  initialSelectedStationIds?: string[];
  initialSelectedResidentIds?: string[];
  selectedFacilityId?: string;
}

const ResidentFilter: React.FC<ResidentFilterProps> = ({
  onCancel,
  onApply,
  initialSelectedStationIds = [],
  initialSelectedResidentIds = [],
  selectedFacilityId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isLargerThan992] = useMediaQuery('(min-width: 992px)');

  // Living areas (stations) state
  const [livingAreas, setLivingAreas] = useState<LivingArea[]>([]);
  const [selectedLivingAreas, setSelectedLivingAreas] = useState<string[]>(
    initialSelectedStationIds
  );
  const [stationsLoading, setStationsLoading] = useState(true);
  const [stationsError, setStationsError] = useState<string | null>(null);

  // Residents state
  const [residents, setResidents] = useState<IResidentListItem[]>([]);
  const [selectedResidents, setSelectedResidents] = useState<string[]>(
    initialSelectedResidentIds
  );
  const [residentsLoading, setResidentsLoading] = useState(true);
  const [residentsError, setResidentsError] = useState<string | null>(null);
  const [selectAllResidents, setSelectAllResidents] = useState(false);

  // Fetch stations data
  useEffect(() => {
    const fetchStations = async () => {
      try {
        setStationsLoading(true);
        const response = await StationService.getListStations(
          1,
          100,
          '',
          selectedFacilityId,
          'true',
          'name',
          'asc'
        );
        const stations = response.items.map((station: IStation) => ({
          id: station.id,
          name: station.name,
        }));
        setLivingAreas(stations);

        // If no initial selection, select all stations
        if (selectedLivingAreas.length === 0) {
          const allStationIds = stations.map((station) => station.id);
          setSelectedLivingAreas(allStationIds);
        }

        setStationsLoading(false);
      } catch (err) {
        setStationsError('Failed to load stations');
        setStationsLoading(false);
        console.error('Error fetching stations:', err);
      }
    };

    fetchStations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update "Alle" (All) option state based on resident selection
  useEffect(() => {
    if (residents.length > 0) {
      setSelectAllResidents(selectedResidents.length === residents.length);
    }
  }, [selectedResidents, residents]);

  // Fetch residents data when selectedLivingAreas changes
  useEffect(() => {
    const fetchResidents = async () => {
      try {
        setResidentsLoading(true);

        // Only apply station_id filter if not all stations are selected
        const params: {
          page?: number;
          per_page?: number;
          station_id?: string;
          carehome_id?: string;
          orderBy?: string;
          sortOrder?: 'asc' | 'desc';
          is_active?: boolean;
        } = {
          page: 1,
          per_page: 100,
          orderBy: 'first_name',
          sortOrder: 'asc',
          is_active: true
        };

        // Apply station filter
        if (selectedLivingAreas.length > 0) {
          params.station_id = selectedLivingAreas.join(',');
        }

        if (selectedFacilityId) {
          params.carehome_id = selectedFacilityId;
        }

        const response = await ResidentService.getResidentList(params);
        const items = (response.items ?? []) as IResidentListItem[];
        setResidents(items);

        const validResidentIds = items.map((resident) => resident.id);
        setSelectedResidents((prevSelected) => {
          if (prevSelected.length === 0 && initialSelectedResidentIds.length > 0) {
            const validSelection = initialSelectedResidentIds.filter((id) =>
              validResidentIds.includes(id)
            );
            setSelectAllResidents(validSelection.length === items.length);
            return validSelection;
          }

          if (prevSelected.length > 0) {
            const validSelection = prevSelected.filter((id) =>
              validResidentIds.includes(id)
            );
            setSelectAllResidents(validSelection.length === items.length);
            return validSelection;
          }

          setSelectAllResidents(false);
          return prevSelected;
        });

        setResidentsLoading(false);
      } catch (err) {
        setResidentsError('Failed to load residents');
        setResidentsLoading(false);
        console.error('Error fetching residents:', err);
      }
    };

    // Only fetch residents if we have fetched living areas
    if (!stationsLoading && livingAreas.length > 0) {
      fetchResidents();
    }
  }, [
    initialSelectedResidentIds,
    livingAreas,
    selectedFacilityId,
    selectedLivingAreas,
    stationsLoading,
  ]);

  // Called when a living area selection changes
  const handleLivingAreaChange = (areaId: string) => {
    const newSelected = [...selectedLivingAreas];

    if (newSelected.includes(areaId)) {
      // If already selected, remove it
      const index = newSelected.indexOf(areaId);
      newSelected.splice(index, 1);
    } else {
      // If not selected, add it
      newSelected.push(areaId);
    }

    setSelectedLivingAreas(newSelected);
  };

  // Called when a resident selection changes
  const handleResidentChange = (residentId: string) => {
    const newSelected = [...selectedResidents];

    if (newSelected.includes(residentId)) {
      // If already selected, remove it
      const index = newSelected.indexOf(residentId);
      newSelected.splice(index, 1);
    } else {
      // If not selected, add it
      newSelected.push(residentId);
    }

    setSelectedResidents(newSelected);

    // Update "Alle" (All) option when all residents are selected
    setSelectAllResidents(newSelected.length === residents.length);
  };

  // Called when "Alle" (All) button is clicked
  const handleSelectAll = () => {
    if (selectAllResidents) {
      // If all are selected, clear all selections
      setSelectedResidents([]);
      setSelectAllResidents(false);
    } else {
      // If none or some are selected, select all
      setSelectedResidents(residents.map((resident) => resident.id));
      setSelectAllResidents(true);
    }
  };

  // Called when "Keine" (None) button is clicked
  const handleSelectNone = () => {
    setSelectedResidents([]);
    setSelectAllResidents(false);
  };

  // Called when Save button is clicked
  const handleApply = () => {
    // Create a mapping of station IDs to names
    const stationNames: Record<string, string> = {};
    livingAreas.forEach((area) => {
      stationNames[area.id] = area.name;
    });

    onApply(selectedLivingAreas, selectedResidents, stationNames);
  };

  // Get formatted resident name
  const getResidentName = (resident: IResidentListItem): string => {
    const firstName = resident.first_name || '';
    const middleName = resident.middle_name || '';
    const lastName = resident.last_name || '';

    let fullName = `${firstName} ${lastName}`;
    if (middleName) {
      fullName = `${firstName} ${middleName} ${lastName}`;
    }

    // If name is too long, truncate it
    if (fullName.length > 25) {
      return fullName.substring(0, 22) + '...';
    }

    return fullName;
  };

  return (
    <Flex flexFlow="column" justifyContent="space-between" alignItems="stretch">
      <Flex
        justifyContent="center"
        flexDirection={isLargerThan992 ? 'row' : 'column'}
      >
        {/* Left side: Living areas (Wohnbereich) */}
        <Box
          width={isLargerThan992 ? '312px' : '100%'}
          mr={isLargerThan992 ? '37.5px' : '0'}
          mb={isLargerThan992 ? '0' : '20px'}
        >
          <Text fontSize="20px" fontWeight="500" mb="15px">
            {t(getLanguageKeyByType('livingAreas', user?.carehome?.type))}
          </Text>
          <Flex flexDirection="column">
            <Text
              fontSize="15px"
              fontWeight="300"
              color="alea.500"
              mb={1}
              pl="15px"
            >
              {t(getLanguageKeyByType('general.wohnbereichsname', user?.carehome?.type))}
            </Text>
            <Box
              borderWidth="1px"
              borderRadius="20px"
              overflow="hidden"
              bg="white"
            >
              {stationsLoading ? (
                <Center py={4}>
                  <Spinner color="#2B4956" size="md" />
                </Center>
              ) : stationsError ? (
                <Center py={4}>
                  <Text color="red.500">{stationsError}</Text>
                </Center>
              ) : (
                <Stack spacing={0}>
                  {livingAreas.map((area) => (
                    <Flex
                      key={area.id}
                      p={3}
                      pl="15px"
                      pr="15px"
                      alignItems="center"
                      justifyContent="space-between"
                      borderBottomWidth={
                        area.id !== livingAreas[livingAreas.length - 1].id
                          ? '1px'
                          : '0'
                      }
                      borderBottomColor="#DCDCDC"
                    >
                      <Text fontSize="16px">{area.name}</Text>
                      <Checkbox
                        isChecked={selectedLivingAreas.includes(area.id)}
                        onChange={() => handleLivingAreaChange(area.id)}
                        colorScheme="alea"
                        size="lg"
                        borderColor="#DCDCDC"
                        sx={{
                          '.chakra-checkbox__control': {
                            borderRadius: '4px',
                            borderWidth: '1px',
                            borderColor: '#DCDCDC',
                            _checked: {
                              bg: '#2B4956',
                              borderColor: '#2B4956',
                            },
                          },
                        }}
                      />
                    </Flex>
                  ))}
                </Stack>
              )}
            </Box>
          </Flex>
        </Box>

        {/* Right side: Residents (Bewohner) */}
        <Box
          width={isLargerThan992 ? '400px' : '100%'}
          ml={isLargerThan992 ? '37.5px' : '0'}
        >
          <Flex justifyContent="space-between" alignItems="center" mb={1}>
            <Text fontSize="20px" fontWeight="500" mb="10px">
              {t(getLanguageKeyByType('residents', user?.carehome?.type))}
            </Text>
            <Flex
              borderWidth="1px"
              borderColor="#DCDCDC"
              borderRadius="full"
              overflow="hidden"
            >
              <Box
                as="button"
                px={4}
                py={1}
                fontSize="14px"
                bg={!selectAllResidents ? '#2B4956' : 'white'}
                color={!selectAllResidents ? 'white' : 'gray.500'}
                borderRadius="0"
                onClick={handleSelectNone}
                _hover={{ bg: !selectAllResidents ? '#2B4956' : 'gray.50' }}
              >
                {t('general.keine')}
              </Box>
              <Box
                as="button"
                px={4}
                py={1}
                fontSize="14px"
                bg={selectAllResidents ? '#2B4956' : 'white'}
                color={selectAllResidents ? 'white' : 'gray.500'}
                borderRadius="0"
                borderLeft="1px solid"
                borderLeftColor="#DCDCDC"
                onClick={handleSelectAll}
                _hover={{ bg: selectAllResidents ? '#2B4956' : 'gray.50' }}
              >
                {t('general.alle')}
              </Box>
            </Flex>
          </Flex>

          <Flex flexDirection="column">
            <Flex mb={1} pl="15px">
              <Text
                width="50px"
                fontSize="15px"
                fontWeight="normal"
                color="alea.500"
                fontStyle="italic"
              >
                {t('general.zi')}
              </Text>
              <Text
                flex="1"
                fontSize="15px"
                fontWeight="normal"
                color="alea.500"
              >
                {t(getLanguageKeyByType('general.bewohnername', user?.carehome?.type))}
              </Text>
            </Flex>

            <Box
              borderWidth="1px"
              borderRadius="20px"
              overflow="auto"
              maxHeight="250px"
              bg="white"
            >
              {residentsLoading ? (
                <Center py={4}>
                  <Spinner color="#2B4956" size="md" />
                </Center>
              ) : residentsError ? (
                <Center py={4}>
                  <Text color="red.500">{residentsError}</Text>
                </Center>
              ) : residents.length === 0 ? (
                <Center py={4}>
                  <Text color="gray.500">{t('resident.noResidents')}</Text>
                </Center>
              ) : (
                <Stack spacing={0}>
                  {residents.map((resident) => (
                    <Flex
                      key={resident.id}
                      p={3}
                      pl="15px"
                      pr="15px"
                      alignItems="center"
                      borderBottomWidth={
                        resident.id !== residents[residents.length - 1].id
                          ? '1px'
                          : '0'
                      }
                      borderBottomColor="#DCDCDC"
                    >
                      <Text width="50px" fontSize="16px" fontStyle="italic">
                        {resident.room}
                      </Text>
                      <Text flex="1" fontSize="16px">
                        {getResidentName(resident)}
                      </Text>
                      <Checkbox
                        isChecked={selectedResidents.includes(resident.id)}
                        onChange={() => handleResidentChange(resident.id)}
                        colorScheme="alea"
                        size="lg"
                        borderColor="#DCDCDC"
                        sx={{
                          '.chakra-checkbox__control': {
                            borderRadius: '4px',
                            borderWidth: '1px',
                            borderColor: '#DCDCDC',
                            _checked: {
                              bg: '#2B4956',
                              borderColor: '#2B4956',
                            },
                          },
                        }}
                      />
                    </Flex>
                  ))}
                </Stack>
              )}
            </Box>
          </Flex>
        </Box>
      </Flex>

      {/* Bottom section: Action buttons */}
      <Box mt={6}>
        <AnalysisFilterActions onCancel={onCancel} onApply={handleApply} />
      </Box>
    </Flex>
  );
};

export default ResidentFilter;
